import { Box, Flex, IconButton, InputText, Text, useToast } from '@driftt/dds'
import { faPaperPlaneTop } from '@driftt/dds.icons/solid/faPaperPlaneTop'
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from 'react-query'
import spacetime from 'spacetime'

import { postPromptToConfig, PromptConfig } from 'api/scenarios'
import { isEmptyOrNil } from 'utils/index'

import { toConversationMessage } from '../utils'
import { BotMessage, BotTypingMessage, EndUserMessage } from './Message'

const AUTHOR_ROLE = {
  USER: 'USER',
  BOT: 'BOT',
}

const AUTHOR_TYPE_TO_MESSAGE_COMPONENT = {
  [AUTHOR_ROLE.USER]: EndUserMessage,
  [AUTHOR_ROLE.BOT]: BotMessage,
}

interface ChatProps {
  botUser: {
    name: string
    avatarUrl: string
  }
  onGenAiServiceError: () => void
}

export const Chat = ({ botUser, onGenAiServiceError }: ChatProps) => {
  const [currentConfiguration, setCurrentConfiguration] =
    useState<PromptConfig | null>(null)
  const [currentConversation, setCurrentConversation] = useState<
    {
      message: string
      role: string
      isFinished?: boolean
      isValidatedPromptType: boolean
    }[]
  >([])
  const [currentEndUserMessage, setCurrentEndUserMessage] = useState<string>('')
  const toast = useToast()

  const sendConfiguration = useMutation({
    mutationFn: postPromptToConfig,
    onSuccess: ({ data }) => {
      const isValidatedPromptType = !isEmptyOrNil(data?.validatedPrompt)
      const message = isValidatedPromptType
        ? data?.validatedPrompt
        : data?.followUpQuestion

      setCurrentConfiguration(data)
      setCurrentConversation((prevConvo) => [
        ...prevConvo,
        toConversationMessage(
          message,
          AUTHOR_ROLE.BOT,
          data?.isFinished,
          isValidatedPromptType
        ),
      ])
    },
    onError: () => {
      onGenAiServiceError()
      toast({
        message:
          'An error ocurred while sending your message, please try again later',
        variant: 'error',
      })
    },
  })

  const chatRef = useRef<HTMLDivElement>()

  useEffect(() => {
    if (!!chatRef.current && !isEmptyOrNil(currentConversation)) {
      chatRef.current.scrollTop += 700
    }
  }, [currentConversation])

  useEffect(() => {
    sendConfiguration.mutate({ agentType: 'SALES' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSendMessage = async () => {
    setCurrentConversation((prevConvo) => [
      ...prevConvo,
      toConversationMessage(currentEndUserMessage, AUTHOR_ROLE.USER),
    ])
    if (!isEmptyOrNil(currentConfiguration)) {
      sendConfiguration.mutate({
        ...currentConfiguration,
        answer: currentEndUserMessage,
      })
    }
    setCurrentEndUserMessage('')
  }

  const isSendInputEnabled = useMemo(
    () => !isEmptyOrNil(currentEndUserMessage),
    [currentEndUserMessage]
  )

  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      fullHeight
      fullWidth
    >
      <Box
        css={{
          overflowY: 'auto',
          background: '$white',
        }}
        fullWidth
        height="100%"
      >
        <Flex
          direction="column"
          css={{
            borderBottomRightRadius: '$half',
            borderBottomLeftRadius: '$half',
            background: '$white',
            padding: '$1 $2',
            overflow: 'auto',
          }}
          className="driftt--ai-widget-chat-container"
          fullHeight
          justifyContent="flex-start"
          alignItems="flex-start"
          //@ts-ignore
          ref={chatRef}
        >
          <Box css={{ textAlign: 'center', marginBottom: '$half' }} fullWidth>
            <Text color="gray3" css={{ fontSize: 10 }}>
              TODAY {spacetime.now().format('h:mm a')}
            </Text>
          </Box>
          {currentConversation.map(
            ({ message, role, isValidatedPromptType }, index) => {
              const Message = AUTHOR_TYPE_TO_MESSAGE_COMPONENT[role]

              return (
                <Fragment key={index}>
                  <Message
                    botUser={botUser}
                    messageString={message}
                    isValidatedPromptType={Boolean(isValidatedPromptType)}
                  />
                </Fragment>
              )
            }
          )}
          {sendConfiguration.isLoading && (
            <BotTypingMessage botUser={botUser} />
          )}
        </Flex>
      </Box>
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        css={{ padding: '$1', background: '$white' }}
        fullWidth
      >
        <InputText
          value={currentEndUserMessage}
          onChange={(event) => {
            const {
              target: { value },
            } = event
            setCurrentEndUserMessage(value)
          }}
          css={{ zoom: 0.9 }}
          //@ts-ignore
          onKeyDown={(event) => {
            if (event.keyCode === 13 && isSendInputEnabled) {
              handleSendMessage()
            }
          }}
          size="full-width"
          placeholder={`Reply to ${botUser.name}`}
        />
        <IconButton
          title="send"
          variant="tertiary"
          onClick={() => handleSendMessage()}
          icon={faPaperPlaneTop}
          disabled={!isSendInputEnabled}
        />
      </Flex>
    </Flex>
  )
}
